import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
} from "@mui/material";
import CustomIcon from "../../common/CustomIcon/CustomIcon";
import { useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import { FC } from "react";
import { IUiSettings } from "../../models/admin/uiSettings/uiSettings";
import RenderVideoBanner from "../../common/RenderVideoBanner/RenderVideoBanner";

export interface IProps {
  formData: IUiSettings;
}
const CurrentUIConfiguration: FC<IProps> = ({ formData }) => {
  const { uiSettings } = useAppSelector((state: RootState) => state.uiSettings);
  const getImageSrc = (image: string | File | undefined) => {
    if (!image) return "";

    if (typeof image === "string") {
      return image.startsWith("data:image") || image.startsWith("http")
        ? image
        : `data:image/png;base64,${image}`;
    }

    return URL.createObjectURL(image);
  };
  return (
    <Card
      id="current_ui_configuration"
      data-test-id="current_ui_configuration"
      aria-label="Current UI Configuration"
    >
      <CardHeader 
        title="Current Configuration" 
        sx={{ textAlign: "center" }}
        id="current_ui_configuration_header"
        data-test-id="current_ui_configuration_header"
        aria-label="Current Configuration Header"
      />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
        id="current_ui_configuration_content"
        data-test-id="current_ui_configuration_content"
        aria-label="Current Configuration Content"
      >
        {formData && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "0.5fr 1fr",
                alignItems: "center",
                gap: "10px",
              }}
              id="current_ui_logo_section"
              data-test-id="current_ui_logo_section"
              aria-label="Logo Section"
            >
              <Typography variant="h6" id="current_ui_logo_section_title" data-test-id="current_ui_logo_section_title" aria-label="Logo Section Title">Logo</Typography>
              {uiSettings && uiSettings.logo && (
                <img
                  src={getImageSrc(uiSettings.logo)}
                  alt="Logo Preview"
                  style={{ maxWidth: "150px", maxHeight: "150px" }}
                  id="current_ui_logo_preview"
                  data-test-id="current_ui_logo_preview"
                />
              )}
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "0.5fr 1fr",
                alignItems: "center",
                gap: "20px",
              }}
              id="current_ui_banner_section"
              data-test-id="current_ui_banner_section"
              aria-label="Login Banner Section"
            >
              <Typography variant="h6" id="current_ui_banner_section_title" data-test-id="current_ui_banner_section_title" aria-label="Login Banner Section Title">Login Banner</Typography>
              {uiSettings &&
                uiSettings?.loginBannerType === "image" &&
                uiSettings?.loginBannerImage && (
                  <img
                    src={getImageSrc(uiSettings?.loginBannerImage)}
                    alt="Login Banner Preview"
                    style={{ maxWidth: "150px", maxHeight: "150px" }}
                    id="current_ui_banner_preview"
                    data-test-id="current_ui_banner_preview"
                  />
                )}
              {uiSettings &&
                uiSettings?.loginBannerType === "video" &&
                uiSettings?.loginBannerVideoUrl && (
                  <RenderVideoBanner
                    videoUrl={uiSettings?.loginBannerVideoUrl}
                  />
                )}
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "0.5fr 1fr",
                alignItems: "center",
                gap: "20px",
              }}
              id="current_ui_loader_section"
              data-test-id="current_ui_loader_section"
              aria-label="Loader Section"
            >
              <Typography variant="h6" id="current_ui_loader_section_title" data-test-id="current_ui_loader_section_title" aria-label="Loader Section Title">Loader</Typography>
              {uiSettings && uiSettings.loader && (
                <img
                  src={getImageSrc(uiSettings.loader)}
                  alt="Loader Preview"
                  style={{ maxWidth: "120px", maxHeight: "120px" }}
                  id="current_ui_loader_preview"
                  data-test-id="current_ui_loader_preview"
                />
              )}
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "0.5fr 1.5fr",
                alignItems: "center",
              }}
              id="current_ui_primary_button_section"
              data-test-id="current_ui_primary_button_section"
              aria-label="Primary Button Section"
            >
              <Typography id="current_ui_primary_button_section_title" data-test-id="current_ui_primary_button_section_title" aria-label="Primary Button Section Title">Primary Button</Typography>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: formData.lightPrimaryMainColor,
                  color: formData.lightPrimaryContrastTextColor,
                  borderRadius: "4px",
                }}
                id="current_ui_primary_button_preview"
                data-test-id="current_ui_primary_button_preview"
                aria-label="Primary Button Preview"
              >
                Text
              </Button>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "0.5fr 1.5fr",
                alignItems: "center",
              }}
              id="current_ui_secondary_button_section"
              data-test-id="current_ui_secondary_button_section"
              aria-label="Secondary Button Section"
            >
              <Typography id="current_ui_secondary_button_section_title" data-test-id="current_ui_secondary_button_section_title" aria-label="Secondary Button Section Title">Secondary Button </Typography>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: formData.lightSecondaryMainColor,
                  color: formData.lightSecondaryContrastTextColor,
                  borderRadius: "4px",
                }}
                id="current_ui_secondary_button_preview"
                data-test-id="current_ui_secondary_button_preview"
                aria-label="Secondary Button Preview"
              >
                Text
              </Button>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "0.5fr 1.5fr",
                alignItems: "center",
              }}
              id="current_ui_icon_section"
              data-test-id="current_ui_icon_section"
              aria-label="Icon Section"
            >
              <Typography id="current_ui_icon_section_title" data-test-id="current_ui_icon_section_title" aria-label="Icon Section Title">Icon</Typography>
              <CustomIcon 
                iconName="RoadsideAssistance"
              />
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CurrentUIConfiguration;
